import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useEffectiveClientId } from '@/hooks/useAuth'
import { functionError } from '@/hooks/useMix'
import type { BrandKit, Family, NewsletterConfig, AdShape } from '@/lib/newsletterEmail'

/**
 * The newsletter: brand kits, saved designs, issues, sponsors and their
 * bookings, Your list (the subscribers GoGrov keeps and sends to, Brian's
 * Oct 29 move off Mailchimp) and the media kit.
 *
 * Everything is scoped to the account by row level security and gated by
 * newsletter_allowed(): included on Grove and Harvest, or switched on by an admin.
 */

export interface Design { id: string; name: string; family: Family; config: NewsletterConfig; brand_kit_id: string | null; updated_at: string }
export interface Issue {
  id: string; family: Family; subject: string; preheader: string | null; status: 'draft' | 'sending' | 'sent' | 'failed'
  provider: string | null; error: string | null; sent_at: string | null; created_at: string; design_id: string | null
  html?: string | null; config?: NewsletterConfig
  /** The last test to the creator's own inbox (newsletter-send, test:true). */
  test_sent_at?: string | null; test_sent_to?: string | null
  /** SHA-256 of subject, preview text and HTML at that test (issueHash). */
  test_hash?: string | null
  /** Resend (20240049): the plain-text part, who it went to, how many got it. */
  plain_text?: string | null; recipients?: number | null; sent_count?: number | null
}
export interface Sponsor { id: string; name: string; contact: string | null; notes: string | null }
export interface AdSlot { id: string; design_id: string | null; slot_key: string; shape: AdShape; label: string | null; rate_cents: number }
export interface Booking {
  id: string; slot_id: string; sponsor_id: string | null; creative_url: string | null
  creative_crop: { x: number; y: number; w: number; h: number } | null
  starts_on: string; ends_on: string; end_action: 'open' | 'house_ad' | 'remind'; rate_cents: number
  status: 'booked' | 'running' | 'ended' | 'cancelled'
}
/** Your list's sender settings (newsletter_lists, migration 20240049). */
export interface NewsletterList {
  client_id: string; handle: string; from_name: string | null; reply_to: string | null; mailing_address: string | null
  send_day: string; send_time: string
}
export interface Subscriber {
  id: string; email: string; name: string | null
  status: 'pending' | 'subscribed' | 'unsubscribed' | 'bounced' | 'complained'
  source: 'import' | 'signup' | 'added'; consent_at: string | null; confirmed_at: string | null; created_at: string
}
export interface ListStats {
  subscribed: number; pending: number; unsubscribed: number; bounced: number
  /** Null until an issue has gone out: nothing is made up. */
  open_rate: number | null
  month_emails: number; month_issues: number
}
export const isEmail = (v: string | null | undefined) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(v ?? '').trim())
/** What Your list still needs before an issue can send, in Brian's order; '' when ready. */
export function listMissing(list: NewsletterList | null | undefined, subscribers: number): string {
  if (!(subscribers > 0)) return 'Add subscribers'
  if (!isEmail(list?.reply_to)) return 'Needs reply-to'
  if (!list?.mailing_address?.trim()) return 'Needs mailing address'
  return ''
}

export function useNewsletterAllowed() {
  const clientId = useEffectiveClientId()
  return useQuery({
    queryKey: ['nl-allowed', clientId],
    enabled: !!clientId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('newsletter_allowed', { p_client_id: clientId! })
      if (error) throw error
      return !!data
    },
  })
}

function table<T>(name: string, key: string, order = 'created_at') {
  return function useTable() {
    const clientId = useEffectiveClientId()
    return useQuery({
      queryKey: [key, clientId],
      enabled: !!clientId,
      queryFn: async () => {
        const { data, error } = await supabase.from(name).select('*').eq('client_id', clientId!).order(order, { ascending: false })
        if (error) throw error
        return data as T[]
      },
    })
  }
}

export const useBrandKits = table<BrandKit & { id: string; is_default: boolean }>('brand_kits', 'nl-brands')
export const useDesigns = table<Design>('newsletter_designs', 'nl-designs', 'updated_at')
export const useIssues = table<Issue>('newsletter_issues', 'nl-issues')
export const useSponsors = table<Sponsor>('sponsors', 'nl-sponsors')
export const useAdSlots = table<AdSlot>('ad_slots', 'nl-slots')

/**
 * Bookings, with expiry evaluated as they are read: a booking past its end
 * date is ended, one inside its dates is running. Nothing runs on a schedule;
 * whoever opens the desk first after the date moves it on.
 */
export function useBookings() {
  const clientId = useEffectiveClientId()
  return useQuery({
    queryKey: ['nl-bookings', clientId],
    enabled: !!clientId,
    queryFn: async () => {
      const { data, error } = await supabase.from('ad_bookings').select('*').eq('client_id', clientId!).order('starts_on')
      if (error) throw error
      const today = new Date().toISOString().slice(0, 10)
      const rows = data as Booking[]
      const updates: Promise<unknown>[] = []
      for (const b of rows) {
        if (b.status === 'cancelled') continue
        const next = b.ends_on < today ? 'ended' : b.starts_on <= today ? 'running' : 'booked'
        if (next !== b.status) {
          b.status = next
          updates.push(Promise.resolve(supabase.from('ad_bookings').update({ status: next }).eq('id', b.id)))
        }
      }
      await Promise.all(updates)
      return rows
    },
  })
}

/** Generic save for the account-owned newsletter tables. */
export function useNlSave<T extends { id?: string }>(tableName: string, key: string) {
  const clientId = useEffectiveClientId()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (row: T) => {
      const { id, ...rest } = row as T & { id?: string }
      const payload = { ...rest, client_id: clientId! }
      const q = id
        ? supabase.from(tableName).update(payload).eq('id', id).select('*').single()
        : supabase.from(tableName).insert(payload).select('*').single()
      const { data, error } = await q
      if (error) throw error
      return data as T & { id: string }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [key, clientId] }),
  })
}

export function useNlDelete(tableName: string, key: string) {
  const clientId = useEffectiveClientId()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from(tableName).delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [key, clientId] }),
  })
}

/** Keeps a saved design's ad inventory in step with the slots it locks. */
export function useSyncSlots() {
  const clientId = useEffectiveClientId()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { designId: string; slots: { key: string; shape: AdShape; label: string; rate_cents?: number }[] }) => {
      const { data: existing, error } = await supabase.from('ad_slots').select('id, slot_key').eq('design_id', args.designId)
      if (error) throw error
      const keep = new Set(args.slots.map((s) => s.key))
      const gone = (existing ?? []).filter((e) => !keep.has(e.slot_key)).map((e) => e.id)
      if (gone.length) await supabase.from('ad_slots').delete().in('id', gone)
      const have = new Set((existing ?? []).map((e) => e.slot_key))
      const add = args.slots.filter((s) => !have.has(s.key))
      if (add.length) {
        const { error: e2 } = await supabase.from('ad_slots').insert(add.map((s) => ({
          client_id: clientId!, design_id: args.designId, slot_key: s.key, shape: s.shape, label: s.label,
          ...(s.rate_cents != null ? { rate_cents: s.rate_cents } : {}),
        })))
        if (e2) throw e2
      }
      // Labels follow the layout, since rows renumber.
      for (const s of args.slots.filter((x) => have.has(x.key))) {
        await supabase.from('ad_slots').update({ label: s.label, shape: s.shape, ...(s.rate_cents != null ? { rate_cents: s.rate_cents } : {}) }).eq('design_id', args.designId).eq('slot_key', s.key)
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['nl-slots', clientId] }),
  })
}

/** The issue as it would go out. */
export interface IssueDraft { family: Family; subject: string; preheader: string; config: NewsletterConfig; html: string; plain_text: string; design_id: string | null }

/**
 * The fingerprint a test is checked against: subject, preview text and HTML,
 * the same SHA-256 newsletter-send stores (esp.ts issueHash). Send refuses if
 * the issue changed after the test.
 */
export async function issueHash(subject: string, preheader: string | null, html: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`${subject}\n${preheader ?? ''}\n${html}`))
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

/** Writes the draft row (new, or the one already being worked on). */
async function saveDraft(clientId: string, id: string | null, issue: IssueDraft): Promise<string> {
  if (id) {
    const { data, error } = await supabase.from('newsletter_issues')
      .update({ ...issue, updated_at: new Date().toISOString() }).eq('id', id).in('status', ['draft', 'failed']).select('id').maybeSingle()
    if (error) throw error
    if (data) return data.id as string
  }
  const { data, error } = await supabase.from('newsletter_issues').insert({ ...issue, client_id: clientId }).select('id').single()
  if (error) throw error
  return data.id as string
}

/**
 * Sends a test of the issue to the creator's own inbox (Brian's checklist:
 * "Send a test to yourself"). Saves the draft first, so the test and the real
 * send are the same row.
 */
export function useTestIssue() {
  const clientId = useEffectiveClientId()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { id: string | null; issue: IssueDraft }) => {
      const id = await saveDraft(clientId!, args.id, args.issue)
      const { data, error } = await supabase.functions.invoke('newsletter-send', { body: { issue_id: id, test: true } })
      if (error) { qc.invalidateQueries({ queryKey: ['nl-issues', clientId] }); throw await functionError(error) }
      return data as { issue: Issue; to: string }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['nl-issues', clientId] }),
  })
}

export function useSendIssue() {
  const clientId = useEffectiveClientId()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { id: string | null; issue: IssueDraft; onProgress?: (sent: number, total: number) => void }) => {
      const id = await saveDraft(clientId!, args.id, args.issue)
      // A long list goes out over several calls: each one sends for up to two
      // minutes and says how far it got; the next picks up after the last person.
      for (let round = 0; round < 60; round++) {
        const { data, error: sendErr } = await supabase.functions.invoke('newsletter-send', { body: { issue_id: id } })
        if (sendErr) {
          qc.invalidateQueries({ queryKey: ['nl-issues', clientId] })
          throw await functionError(sendErr)
        }
        const r = data as { issue?: Issue; partial?: boolean; sent?: number; total?: number }
        if (!r.partial) return r as { issue: Issue; sent?: number }
        args.onProgress?.(r.sent ?? 0, r.total ?? 0)
      }
      throw new Error('The send is taking longer than expected. It carries on where it stopped when you press Send again.')
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['nl-issues', clientId] })
      qc.invalidateQueries({ queryKey: ['coin-case'] })
    },
  })
}

// ---------- Your list (Brian's settings-newsletter.html v2, Oct 29) ----------

/** The sender: made on first open (newsletter_list_get). */
export function useYourList(enabled = true) {
  const clientId = useEffectiveClientId()
  return useQuery({
    queryKey: ['nl-list', clientId],
    enabled: !!clientId && enabled,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('newsletter_list_get' as never)
      if (error) throw error
      return data as unknown as NewsletterList
    },
  })
}

export function useListStats(enabled = true) {
  const clientId = useEffectiveClientId()
  return useQuery({
    queryKey: ['nl-list-stats', clientId],
    enabled: !!clientId && enabled,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('newsletter_list_stats' as never)
      if (error) throw error
      return data as unknown as ListStats
    },
  })
}

export function useListUpdate() {
  const clientId = useEffectiveClientId()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (patch: Partial<Pick<NewsletterList, 'from_name' | 'reply_to' | 'mailing_address' | 'send_day' | 'send_time'>>) => {
      const { data, error } = await supabase.rpc('newsletter_list_update' as never, { p_patch: patch } as never)
      if (error) throw error
      return data as unknown as NewsletterList
    },
    onSuccess: (row) => qc.setQueryData(['nl-list', clientId], row),
  })
}

/** Everyone on the list, newest first (for Download and the people sheet). */
export function useSubscribers(enabled: boolean) {
  const clientId = useEffectiveClientId()
  return useQuery({
    queryKey: ['nl-subs', clientId],
    enabled: !!clientId && enabled,
    queryFn: async () => {
      const out: Subscriber[] = []
      for (let from = 0; ; from += 1000) {
        const { data, error } = await supabase.from('newsletter_subscribers')
          .select('id, email, name, status, source, consent_at, confirmed_at, created_at')
          .eq('client_id', clientId!).order('created_at', { ascending: false }).range(from, from + 999)
        if (error) throw error
        out.push(...(data as Subscriber[]))
        if ((data?.length ?? 0) < 1000) break
      }
      return out
    },
  })
}

function useListChanged() {
  const clientId = useEffectiveClientId()
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: ['nl-list-stats', clientId] })
    qc.invalidateQueries({ queryKey: ['nl-subs', clientId] })
    qc.invalidateQueries({ queryKey: ['coin-case'] })
  }
}

/** Import: a CSV or a paste, only with "These people asked to get my emails". */
export function useImportSubscribers() {
  const changed = useListChanged()
  return useMutation({
    mutationFn: async (args: { rows: { email: string; name?: string | null }[]; consent: boolean }) => {
      const { data, error } = await supabase.rpc('newsletter_import' as never, { p_rows: args.rows, p_consent: args.consent } as never)
      if (error) throw error
      return data as unknown as { added: number; duplicates: number; invalid: number; role: number }
    },
    onSuccess: changed,
  })
}

/** Add one person: they get the confirm email and join when they click it. */
export function useAddSubscriber() {
  const changed = useListChanged()
  return useMutation({
    mutationFn: async (args: { email: string; name?: string }) => {
      const { data, error } = await supabase.functions.invoke('newsletter-list', { body: { action: 'add', ...args } })
      if (error) throw await functionError(error)
      return data as { pending?: boolean; already?: boolean }
    },
    onSuccess: changed,
  })
}

export function useRemoveSubscriber() {
  const changed = useListChanged()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc('newsletter_subscriber_remove' as never, { p_id: id } as never)
      if (error) throw error
    },
    onSuccess: changed,
  })
}

/** Reads a CSV or a paste: the column headed email (else the first that holds
 *  addresses) and, if there is one, a name column. Quotes are handled. */
export function parseList(text: string): { email: string; name: string | null }[] {
  const rows = text.replace(/\r\n?/g, '\n').split('\n').filter((l) => l.trim())
  const split = (line: string) => {
    const out: string[] = []; let cur = ''; let q = false
    for (let i = 0; i < line.length; i++) {
      const ch = line[i]
      if (q) { if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++ } else if (ch === '"') q = false; else cur += ch }
      else if (ch === '"') q = true
      else if (ch === ',' || ch === ';' || ch === '\t') { out.push(cur.trim()); cur = '' }
      else cur += ch
    }
    out.push(cur.trim())
    return out
  }
  if (!rows.length) return []
  const head = split(rows[0]).map((h) => h.toLowerCase())
  let ei = head.findIndex((h) => /e-?mail/.test(h))
  const hasHead = ei >= 0 || !head.some((h) => h.includes('@'))
  const body = hasHead ? rows.slice(1) : rows
  if (ei < 0) ei = Math.max(0, split(body[0] ?? '').findIndex((c) => c.includes('@')))
  const first = head.findIndex((h) => /first/.test(h)), last = head.findIndex((h) => /last/.test(h))
  const ni = hasHead ? head.findIndex((h, i) => i !== ei && /name/.test(h) && !/first|last|user/.test(h)) : -1
  return body.map((l) => {
    const c = split(l)
    const nm = ni >= 0 ? c[ni] : hasHead && (first >= 0 || last >= 0) ? [c[first] ?? '', c[last] ?? ''].join(' ').trim() : ''
    return { email: (c[ei] ?? '').trim().toLowerCase(), name: nm || null }
  }).filter((r) => r.email.includes('@'))
}

/** Uploads an image into the account's own folder of the public bucket. */
export async function uploadNewsletterImage(clientId: string, file: File, kind: string): Promise<string> {
  if (!/^image\/(jpeg|png|webp|gif)$/.test(file.type)) throw new Error('Use a JPEG, PNG, WebP or GIF.')
  if (file.size > 5 * 1024 * 1024) throw new Error('That image is over 5 MB.')
  const ext = file.name.split('.').pop()?.toLowerCase() || 'png'
  const path = `${clientId}/${kind}-${crypto.randomUUID()}.${ext}`
  const { error } = await supabase.storage.from('newsletter').upload(path, file, { contentType: file.type, upsert: false })
  if (error) throw error
  return supabase.storage.from('newsletter').getPublicUrl(path).data.publicUrl
}

export function useCreateMediaKit() {
  return useMutation({
    mutationFn: async (snapshot: unknown) => {
      const { data, error } = await supabase.rpc('create_media_kit', { p_snapshot: snapshot })
      if (error) throw error
      return data as string
    },
  })
}

export function useMediaKit(token: string | undefined) {
  return useQuery({
    queryKey: ['media-kit', token],
    enabled: !!token,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_media_kit', { p_token: token! })
      if (error) throw error
      return data as { snapshot: MediaKitSnapshot; publication: string; place: string; created_at: string } | null
    },
  })
}

export interface MediaKitSnapshot {
  html: string
  spots: { label: string; shape: AdShape; rate: string; status: 'open' | 'taken'; frees_on: string | null; next_rate: string | null }[]
  contact: string | null
  audience: string | null
}

export const money = (cents: number) => `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`

/** Whether GoGrov's sending is set up (the admin's Resend key and domain), and
 *  the domain each creator sends from. Never the key itself. */
export function useSending() {
  return useQuery({
    queryKey: ['nl-sending'],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('newsletter_sending' as never)
      if (error) throw error
      return data as unknown as { domain: string | null; configured: boolean }
    },
  })
}
